import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useMutation, useResource } from '../api/hooks';
import type { ServerSummary } from '../api/types';
import { useI18n } from '../i18n';
import { useSession } from '../auth/SessionProvider';
import { Badge, Button, Card, Empty, Field, Spinner, TextInput } from '../components/ui';
import { ErrorBanner } from '../components/ErrorBanner';
import { SecretReveal } from '../components/SecretReveal';

type CreateResponse = { id: string; name: string; pairingToken: string };

export function ServersPage() {
  const { t } = useI18n();
  const { me } = useSession();
  const navigate = useNavigate();
  const servers = useResource<ServerSummary[]>('/v1/servers');

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [freshToken, setFreshToken] = useState<{ token: string; serverId: string } | null>(null);

  const create = useMutation<string, CreateResponse>(
    useCallback((value: string) => api.post<CreateResponse>('/v1/servers', { name: value }), []),
  );
  const join = useMutation<string, { id: string }>(
    useCallback(
      (value: string) => api.post<{ id: string }>('/v1/invites/accept', { code: value }),
      [],
    ),
  );

  const owned = (servers.data ?? []).filter((server) => server.role === 'OWNER').length;
  const quotaReached = me !== null && owned >= me.quotas.servers;

  const onCreate = async () => {
    const created = await create.run(name.trim());
    if (!created) return;
    setName('');
    // Le token n'existera plus nulle part après cette réponse : on l'affiche avant de
    // rafraîchir quoi que ce soit, et l'utilisateur doit acquitter pour le faire
    // disparaître.
    setFreshToken({ token: created.pairingToken, serverId: created.id });
    servers.reload();
  };

  const onJoin = async () => {
    const joined = await join.run(code.trim());
    if (!joined) return;
    setCode('');
    servers.reload();
    navigate(`/serveur/${joined.id}`);
  };

  if (servers.loading) return <Spinner label={t('common.loading')} />;

  return (
    <div className="stack">
      <h1>{t('servers.title')}</h1>

      {freshToken ? (
        <SecretReveal
          secret={freshToken.token}
          intro={t('servers.tokenCreated')}
          onAcknowledge={() => {
            const target = freshToken.serverId;
            setFreshToken(null);
            navigate(`/serveur/${target}`);
          }}
        />
      ) : null}

      <ErrorBanner error={servers.error} />

      {(servers.data ?? []).length === 0 ? (
        <Empty>{t('servers.none')}</Empty>
      ) : (
        <div className="grid">
          {(servers.data ?? []).map((server) => (
            <Card key={server.id} title={<Link to={`/serveur/${server.id}`}>{server.name}</Link>}>
              <dl className="pairs">
                <dt>{t('servers.role')}</dt>
                <dd>
                  <Badge tone={server.role === 'OWNER' ? 'good' : 'neutral'}>{server.role}</Badge>
                </dd>
                <dt>{t('servers.members')}</dt>
                <dd>{server.memberCount}</dd>
              </dl>
            </Card>
          ))}
        </div>
      )}

      {!quotaReached ? (
        <Card title={t('servers.create')}>
          <ErrorBanner error={create.error} />
          <Field label={t('servers.nameLabel')}>
            <TextInput
              value={name}
              maxLength={64}
              placeholder={t('servers.namePlaceholder')}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Button
            variant="primary"
            pending={create.pending}
            disabled={name.trim().length < 3}
            onClick={() => void onCreate()}
          >
            {t('common.create')}
          </Button>
        </Card>
      ) : (
        <Card title={t('servers.create')}>
          <p className="muted">{t('servers.quotaReached')}</p>
        </Card>
      )}

      <Card title={t('server.joinTitle')}>
        <ErrorBanner error={join.error} />
        <Field label={t('server.joinCode')}>
          <TextInput value={code} onChange={(event) => setCode(event.target.value)} />
        </Field>
        <Button
          pending={join.pending}
          disabled={code.trim().length < 8}
          onClick={() => void onJoin()}
        >
          {t('server.join')}
        </Button>
      </Card>
    </div>
  );
}
